import React, { useEffect, useState } from "react";
import {
  Modal,
  Box,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Divider,
  Grid,
  Paper,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Chip,
  Avatar,
  Fade,
  Slide,
  Zoom
} from "@mui/material";
import {
  Close,
  CalendarMonth,
  Schedule,
  Link as LinkIcon,
  Star,
  TrendingUp,
  People,
  PersonOutline,
  GroupWork,
  DateRange,
  Launch,
  CheckCircle,
  RadioButtonUnchecked,
  RocketLaunch
} from "@mui/icons-material";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
dayjs.extend(utc);
dayjs.extend(isSameOrAfter);

const CreateMasterclassModal = ({
  open,
  onClose,
  onSubmit,
  loading = false,
  editData = null
}) => {
  const [formData, setFormData] = useState({
    title: "First Masterclass",
    type: "",
    startDate: "",
    endDate: "",
    coachVaultStartDate: "",
    bundleAmount: 0,
    splitAmount: 0,
    threeSplitAmount: 0,
    level1_start_date: "",
    level2_start_date: "",
    level3_start_date: "",
    link: {
      level1: "",
      level2: "",
      level3: ""
    }
  });

  useEffect(() => {
    if (editData) {
      setFormData({
        title: editData.title || "",
        type: editData.type || "",
        // Convert to UTC date string for input fields
        startDate: editData.startDate
          ? dayjs.utc(editData.startDate).format("YYYY-MM-DD")
          : "",
        endDate: editData.endDate
          ? dayjs.utc(editData.endDate).format("YYYY-MM-DD")
          : "",
        coachVaultStartDate: editData.coachVaultStartDate
          ? dayjs.utc(editData.coachVaultStartDate).format("YYYY-MM-DD")
          : "",
        splitAmount: editData.splitAmount || 0,
        bundleAmount: editData.bundleAmount || 0,
        threeSplitAmount: editData.threeSplitAmount || 0,
        level1_start_date: editData.level1_start_date
          ? dayjs.utc(editData.level1_start_date).format("YYYY-MM-DD")
          : "",
        level2_start_date: editData.level2_start_date
          ? dayjs.utc(editData.level2_start_date).format("YYYY-MM-DD")
          : "",
        level3_start_date: editData.level3_start_date
          ? dayjs.utc(editData.level3_start_date).format("YYYY-MM-DD")
          : "",
        link: {
          level1: editData.link?.level1 || "",
          level2: editData.link?.level2 || "",
          level3: editData.link?.level3 || ""
        }
      });
    } else {
      setFormData({
        title: "",
        type: "",
        startDate: "",
        endDate: "",
        coachVaultStartDate: "",
        splitAmount: 0,
        bundleAmount: 0,
        threeSplitAmount: 0,
        level1_start_date: "",
        level2_start_date: "",
        level3_start_date: "",
        link: {
          level1: "",
          level2: "",
          level3: ""
        }
      });
    }
  }, [editData]);

  const [errors, setErrors] = useState({});

  const masterclassTypes = [
    {
      value: "individuals",
      label: "Individuals",
      icon: <PersonOutline />,
      color: "#1976d2",
      description: "Perfect for personal growth"
    },
    {
      value: "coaches",
      label: "Coaches",
      icon: <GroupWork />,
      color: "#9c27b0",
      description: "Designed for coaching professionals"
    },
    {
      value: "accelerator",
      label: "Individuals - Accelerator",
      icon: <RocketLaunch />,
      color: "#4caf50",
      description: "For individuals who want to take their fitness to the next level"
    }
  ];

  const levels = [
    {
      key: "level1",
      title: "800g Challenge",
      name: "Level 1",
      icon: <Star />,
      color: "#ff9800",
      gradient: "linear-gradient(135deg, #ff9800 0%, #ffb74d 100%)",
      description: "Foundation level program"
    },
    {
      key: "level2",
      title: "Lazy Macros",
      name: "Level 2",
      icon: <TrendingUp />,
      color: "#4caf50",
      gradient: "linear-gradient(135deg, #4caf50 0%, #81c784 100%)",
      description: "Intermediate progression"
    },
    {
      key: "level3",
      title: "Three Pillars Method",
      name: "Level 3",
      icon: <People />,
      color: "#e91e63",
      gradient: "linear-gradient(135deg, #e91e63 0%, #f06292 100%)",
      description: "Advanced mastery level"
    }
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name.startsWith("link.")) {
      const linkLevel = name.split(".")[1];
      setFormData((prev) => ({
        ...prev,
        link: {
          ...prev.link,
          [linkLevel]: value
        }
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value
      }));
    }

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: ""
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.title.trim()) newErrors.title = "Title is required";
    if (!formData.type) newErrors.type = "Type is required";
    if (!formData.startDate) newErrors.startDate = "Start date is required";
    if (!formData.endDate) newErrors.endDate = "End date is required";
    if (!formData.coachVaultStartDate && formData.type === "coaches")
      newErrors.coachVaultStartDate = "Coach Vault start date is required";
    if (!formData.level1_start_date)
      newErrors.level1_start_date = "Level 1 start date is required";
    if (!formData.level2_start_date)
      newErrors.level2_start_date = "Level 2 start date is required";
    if (!formData.level3_start_date)
      newErrors.level3_start_date = "Level 3 start date is required";

    // Validate date logic
    if (formData.startDate && formData.endDate) {
      if (dayjs(formData.startDate).isSameOrAfter(dayjs(formData.endDate))) {
        newErrors.endDate = "End date must be after start date";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      const payload = {
        ...formData,
        startDate: dayjs.utc(formData.startDate).toISOString(),
        endDate: dayjs.utc(formData.endDate).toISOString(),
        coachVaultStartDate: formData.coachVaultStartDate
          ? dayjs.utc(formData.coachVaultStartDate).toISOString()
          : null,
        level1_start_date: dayjs.utc(formData.level1_start_date).toISOString(),
        level2_start_date: dayjs.utc(formData.level2_start_date).toISOString(),
        level3_start_date: dayjs.utc(formData.level3_start_date).toISOString(),
        id: editData?._id
      };
      onSubmit(payload);
    }
  };

  const handleClose = () => {
    setFormData({
      title: "",
      type: "",
      startDate: "",
      endDate: "",
      bundleAmount: 0,
      splitAmount: 0,
      threeSplitAmount: 0,
      level1_start_date: "",
      level2_start_date: "",
      level3_start_date: "",
      link: {
        level1: "",
        level2: "",
        level3: ""
      }
    });
    setErrors({});
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center"
      }}
    >
      <Fade in={open} timeout={400}>
        <Box
          sx={{
            bgcolor: "background.paper",
            borderRadius: 4,
            width: { xs: "95%", sm: "90%", md: "85%" },
            maxWidth: "1000px",
            maxHeight: "95vh",
            overflow: "auto",
            boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
            position: "relative",
            background: "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)"
          }}
        >
          {/* Enhanced Header with Gradient */}
          <Box
            sx={{
              background:
                "linear-gradient(135deg, #1976d2 0%, #1565c0 50%, #0d47a1 100%)",
              color: "white",
              p: 4,
              borderRadius: "16px 16px 0 0",
              position: "relative",
              overflow: "hidden",
              "&::before": {
                content: '""',
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background:
                  "radial-gradient(circle at 30% 20%, rgba(255,255,255,0.1) 0%, transparent 50%)",
                pointerEvents: "none"
              }
            }}
          >
            <Box sx={{ position: "relative", zIndex: 1 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <Box>
                    <Typography variant="h4" fontWeight={700} sx={{ mb: 0.5 }}>
                      {editData ? "Edit" : "Create"} Masterclass
                    </Typography>
                    <Typography variant="subtitle1" sx={{ opacity: 0.9 }}>
                      Configure your masterclass settings and levels
                    </Typography>
                  </Box>
                </Box>
                <IconButton
                  onClick={handleClose}
                  sx={{
                    color: "white",
                    bgcolor: "rgba(255,255,255,0.1)",
                    "&:hover": { bgcolor: "rgba(255,255,255,0.2)" },
                    backdropFilter: "blur(10px)"
                  }}
                >
                  <Close />
                </IconButton>
              </Box>

              {editData && (
                <Chip
                  label="Editing Mode"
                  sx={{
                    bgcolor: "rgba(255,255,255,0.2)",
                    color: "white",
                    fontWeight: 600
                  }}
                />
              )}
            </Box>
          </Box>

          <Box sx={{ p: 4 }}>
            {/* Enhanced Basic Information Section */}
            <Slide direction="up" in={true} timeout={600}>
              <Card
                elevation={0}
                sx={{
                  mb: 4,
                  borderRadius: 3,
                  background:
                    "linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)",
                  border: "1px solid #e2e8f0",
                  position: "relative",
                  overflow: "hidden",
                  "&::before": {
                    content: '""',
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "4px",
                    height: "100%",
                    background: "linear-gradient(to bottom, #1976d2, #42a5f5)"
                  }
                }}
              >
                <CardContent sx={{ p: 4, pl: 5 }}>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                    <Avatar
                      sx={{ bgcolor: "#1976d2", mr: 2, width: 40, height: 40 }}
                    >
                      <CalendarMonth />
                    </Avatar>
                    <Box>
                      <Typography
                        variant="h5"
                        fontWeight={700}
                        color="#1e293b"
                        sx={{ mb: 0.5 }}
                      >
                        Basic Information
                      </Typography>
                      <Typography variant="body2" color="#64748b">
                        Set up the core details for your masterclass
                      </Typography>
                    </Box>
                  </Box>

                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Masterclass Title"
                        name="title"
                        value={formData.title}
                        onChange={handleInputChange}
                        error={!!errors.title}
                        helperText={errors.title}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            backgroundColor: "#ffffff",
                            borderRadius: 2,
                            "& fieldset": {
                              borderColor: "#e2e8f0"
                            },
                            "&:hover fieldset": {
                              borderColor: "#1976d2"
                            },
                            "&.Mui-focused fieldset": {
                              borderColor: "#1976d2",
                              borderWidth: 2
                            }
                          },
                          "& .MuiInputLabel-root": {
                            fontWeight: 600
                          }
                        }}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <FormControl fullWidth error={!!errors.type}>
                        <InputLabel sx={{ fontWeight: 600 }}>
                          User Type
                        </InputLabel>
                        <Select
                          name="type"
                          value={formData.type}
                          onChange={handleInputChange}
                          label="User Type"
                          sx={{
                            backgroundColor: "#ffffff",
                            borderRadius: 2,
                            "& .MuiOutlinedInput-notchedOutline": {
                              borderColor: "#e2e8f0"
                            },
                            "&:hover .MuiOutlinedInput-notchedOutline": {
                              borderColor: "#1976d2"
                            },
                            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                              borderColor: "#1976d2",
                              borderWidth: 2
                            }
                          }}
                        >
                          {masterclassTypes.map((type) => (
                            <MenuItem
                              key={type.value}
                              value={type.value}
                              sx={{ py: 2 }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 2,
                                  width: "100%"
                                }}
                              >
                                <Avatar
                                  sx={{
                                    bgcolor: type.color,
                                    width: 32,
                                    height: 32
                                  }}
                                >
                                  {type.icon}
                                </Avatar>
                                <Box sx={{ flex: 1 }}>
                                  <Typography variant="body1" fontWeight={600}>
                                    {type.label}
                                  </Typography>
                                </Box>
                              </Box>
                            </MenuItem>
                          ))}
                        </Select>
                        {errors.type && (
                          <Typography
                            variant="caption"
                            color="error"
                            sx={{ mt: 0.5, ml: 1.5 }}
                          >
                            {errors.type}
                          </Typography>
                        )}
                      </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        type="number"
                        onWheel={(e) => e.target.blur()}
                        label="One time Amount"
                        name="bundleAmount"
                        value={formData.bundleAmount}
                        onChange={handleInputChange}
                        error={!!errors.bundleAmount}
                        helperText={errors.bundleAmount}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            backgroundColor: "#ffffff",
                            borderRadius: 2,
                            "& fieldset": {
                              borderColor: "#e2e8f0"
                            },
                            "&:hover fieldset": {
                              borderColor: "#1976d2"
                            },
                            "&.Mui-focused fieldset": {
                              borderColor: "#1976d2",
                              borderWidth: 2
                            }
                          },
                          "& .MuiInputLabel-root": {
                            fontWeight: 600
                          }
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        type="number"
                        onWheel={(e) => e.target.blur()}
                        label="Two Split Payment Amount"
                        name="splitAmount"
                        value={formData.splitAmount}
                        onChange={handleInputChange}
                        error={!!errors.splitAmount}
                        helperText={errors.splitAmount}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            backgroundColor: "#ffffff",
                            borderRadius: 2,
                            "& fieldset": {
                              borderColor: "#e2e8f0"
                            },
                            "&:hover fieldset": {
                              borderColor: "#1976d2"
                            },
                            "&.Mui-focused fieldset": {
                              borderColor: "#1976d2",
                              borderWidth: 2
                            }
                          },
                          "& .MuiInputLabel-root": {
                            fontWeight: 600
                          }
                        }}
                      />
                    </Grid>
                   
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        type="number"
                        onWheel={(e) => e.target.blur()}
                        label="Three Split Payment Amount"
                        name="threeSplitAmount"
                        value={formData.threeSplitAmount}
                        onChange={handleInputChange}
                        error={!!errors.threeSplitAmount}
                        helperText={errors.threeSplitAmount}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            backgroundColor: "#ffffff",
                            borderRadius: 2,
                            "& fieldset": {
                              borderColor: "#e2e8f0"
                            },
                            "&:hover fieldset": {
                              borderColor: "#1976d2"
                            },
                            "&.Mui-focused fieldset": {
                              borderColor: "#1976d2",
                              borderWidth: 2
                            }
                          },
                          "& .MuiInputLabel-root": {
                            fontWeight: 600
                          }
                        }}
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Slide>

            {/* Enhanced Duration Settings */}
            <Zoom in={true} timeout={700}>
              <Card
                elevation={0}
                sx={{
                  mb: 4,
                  borderRadius: 3,
                  background:
                    "linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)",
                  border: "1px solid #bfdbfe",
                  position: "relative",
                  overflow: "hidden",
                  "&::before": {
                    content: '""',
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "4px",
                    height: "100%",
                    background: "linear-gradient(to bottom, #3b82f6, #60a5fa)"
                  }
                }}
              >
                <CardContent sx={{ p: 4, pl: 5 }}>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                    <Avatar
                      sx={{ bgcolor: "#3b82f6", mr: 2, width: 40, height: 40 }}
                    >
                      <Schedule />
                    </Avatar>
                    <Box>
                      <Typography
                        variant="h5"
                        fontWeight={700}
                        color="#1e40af"
                        sx={{ mb: 0.5 }}
                      >
                        Duration Settings
                      </Typography>
                      <Typography
                        variant="body2"
                        color="#1e40af"
                        sx={{ opacity: 0.8 }}
                      >
                        Define the start and end dates for your masterclass
                      </Typography>
                    </Box>
                  </Box>

                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Start Date"
                        name="startDate"
                        type="date"
                        value={formData.startDate}
                        onChange={handleInputChange}
                        error={!!errors.startDate}
                        helperText={errors.startDate}
                        InputLabelProps={{ shrink: true }}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            backgroundColor: "#ffffff",
                            borderRadius: 2,
                            "& fieldset": {
                              borderColor: "#bfdbfe"
                            },
                            "&:hover fieldset": {
                              borderColor: "#3b82f6"
                            },
                            "&.Mui-focused fieldset": {
                              borderColor: "#3b82f6",
                              borderWidth: 2
                            }
                          },
                          "& .MuiInputLabel-root": {
                            fontWeight: 600
                          }
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="End Date"
                        name="endDate"
                        type="date"
                        value={formData.endDate}
                        onChange={handleInputChange}
                        error={!!errors.endDate}
                        helperText={errors.endDate}
                        InputLabelProps={{ shrink: true }}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            backgroundColor: "#ffffff",
                            borderRadius: 2,
                            "& fieldset": {
                              borderColor: "#bfdbfe"
                            },
                            "&:hover fieldset": {
                              borderColor: "#3b82f6"
                            },
                            "&.Mui-focused fieldset": {
                              borderColor: "#3b82f6",
                              borderWidth: 2
                            }
                          },
                          "& .MuiInputLabel-root": {
                            fontWeight: 600
                          }
                        }}
                      />
                    </Grid>
                    {formData.type === "coaches" && (
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Coach Vault Start Date"
                          name="coachVaultStartDate"
                          type="date"
                          value={formData.coachVaultStartDate}
                          onChange={handleInputChange}
                          error={!!errors.coachVaultStartDate}
                          helperText={errors.coachVaultStartDate}
                          InputLabelProps={{ shrink: true }}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              backgroundColor: "#ffffff",
                              borderRadius: 2,
                              "& fieldset": {
                                borderColor: "#bfdbfe"
                              },
                              "&:hover fieldset": {
                                borderColor: "#3b82f6"
                              },
                              "&.Mui-focused fieldset": {
                                borderColor: "#3b82f6",
                                borderWidth: 2
                              }
                            },
                            "& .MuiInputLabel-root": {
                              fontWeight: 600
                            }
                          }}
                        />
                      </Grid>
                    )}
                  </Grid>
                </CardContent>
              </Card>
            </Zoom>

            {/* Enhanced Level Start Dates */}
            <Slide direction="left" in={true} timeout={800}>
              <Card
                elevation={0}
                sx={{
                  mb: 4,
                  borderRadius: 3,
                  background:
                    "linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)",
                  border: "1px solid #bbf7d0",
                  position: "relative",
                  overflow: "hidden",
                  "&::before": {
                    content: '""',
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "4px",
                    height: "100%",
                    background: "linear-gradient(to bottom, #059669, #34d399)"
                  }
                }}
              >
                <CardContent sx={{ p: 4, pl: 5 }}>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                    <Avatar
                      sx={{ bgcolor: "#059669", mr: 2, width: 40, height: 40 }}
                    >
                      <DateRange />
                    </Avatar>
                    <Box>
                      <Typography
                        variant="h5"
                        fontWeight={700}
                        color="#059669"
                        sx={{ mb: 0.5 }}
                      >
                        Level Start Dates
                      </Typography>
                      <Typography
                        variant="body2"
                        color="#059669"
                        sx={{ opacity: 0.8 }}
                      >
                        Configure when each level becomes available
                      </Typography>
                    </Box>
                  </Box>

                  <Grid container spacing={3}>
                    {levels.map((level, index) => (
                      <Grid item xs={12} md={4} key={level.key}>
                        <TextField
                          fullWidth
                          label={`${level.title} Start Date`}
                          name={`${level.key}_start_date`}
                          type="date"
                          value={formData[`${level.key}_start_date`]}
                          onChange={handleInputChange}
                          error={!!errors[`${level.key}_start_date`]}
                          helperText={errors[`${level.key}_start_date`]}
                          InputLabelProps={{ shrink: true }}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              backgroundColor: "#ffffff",
                              borderRadius: 2,
                              "& fieldset": {
                                borderColor: "#bbf7d0"
                              },
                              "&:hover fieldset": {
                                borderColor: level.color
                              },
                              "&.Mui-focused fieldset": {
                                borderColor: level.color,
                                borderWidth: 2
                              }
                            },
                            "& .MuiInputLabel-root": {
                              fontWeight: 600
                            }
                          }}
                        />
                      </Grid>
                    ))}
                  </Grid>
                </CardContent>
              </Card>
            </Slide>

            {/* Enhanced Community Links */}
            <Slide direction="right" in={true} timeout={900}>
              <Card
                elevation={0}
                sx={{
                  mb: 4,
                  borderRadius: 3,
                  background:
                    "linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)",
                  border: "1px solid #fecaca",
                  position: "relative",
                  overflow: "hidden",
                  "&::before": {
                    content: '""',
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "4px",
                    height: "100%",
                    background: "linear-gradient(to bottom, #dc2626, #f87171)"
                  }
                }}
              >
                <CardContent sx={{ p: 4, pl: 5 }}>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                    <Avatar
                      sx={{ bgcolor: "#dc2626", mr: 2, width: 40, height: 40 }}
                    >
                      <LinkIcon />
                    </Avatar>
                    <Box>
                      <Typography
                        variant="h5"
                        fontWeight={700}
                        color="#dc2626"
                        sx={{ mb: 0.5 }}
                      >
                        Community Links
                      </Typography>
                      <Typography
                        variant="body2"
                        color="#dc2626"
                        sx={{ opacity: 0.8 }}
                      >
                        Optional community links for each level
                      </Typography>
                    </Box>
                    <Chip
                      label="Optional"
                      size="small"
                      sx={{
                        ml: "auto",
                        bgcolor: "rgba(220, 38, 38, 0.1)",
                        color: "#dc2626",
                        fontWeight: 600
                      }}
                    />
                  </Box>

                  <Grid container spacing={3}>
                    {levels.map((level) => (
                      <Grid item xs={12} md={4} key={level.key}>
                        <TextField
                          fullWidth
                          label={`${level.title} Community Link`}
                          name={`link.${level.key}`}
                          value={formData.link[level.key]}
                          onChange={handleInputChange}
                          placeholder="https://example.com"
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              backgroundColor: "#ffffff",
                              borderRadius: 2,
                              "& fieldset": {
                                borderColor: "#fecaca"
                              },
                              "&:hover fieldset": {
                                borderColor: level.color
                              },
                              "&.Mui-focused fieldset": {
                                borderColor: level.color,
                                borderWidth: 2
                              }
                            },
                            "& .MuiInputLabel-root": {
                              fontWeight: 600
                            }
                          }}
                        />
                      </Grid>
                    ))}
                  </Grid>
                </CardContent>
              </Card>
            </Slide>

            {Object.keys(errors).length > 0 && (
              <Alert
                severity="error"
                sx={{
                  mb: 3,
                  borderRadius: 2,
                  "& .MuiAlert-message": {
                    fontWeight: 600
                  }
                }}
              >
                Please fix the errors above before submitting.
              </Alert>
            )}
          </Box>

          {/* Enhanced Footer */}
          <Box
            sx={{
              background: "linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)",
              borderTop: "1px solid #e2e8f0",
              p: 4,
              borderRadius: "0 0 16px 16px"
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center"
              }}
            >
              <Box sx={{ display: "flex", gap: 2 }}>
                <Button
                  variant="outlined"
                  onClick={handleClose}
                  disabled={loading}
                  sx={{
                    minWidth: 120,
                    height: 48,
                    color: "#64748b",
                    borderColor: "#cbd5e1",
                    fontWeight: 600,
                    borderRadius: 3,
                    "&:hover": {
                      borderColor: "#94a3b8",
                      backgroundColor: "#f8fafc",
                      transform: "translateY(-1px)"
                    },
                    transition: "all 0.2s ease-in-out"
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={handleSubmit}
                  disabled={loading}
                  sx={{
                    minWidth: 150,
                    height: 48,
                    background:
                      "linear-gradient(135deg, #1976d2 0%, #1565c0 100%)",
                    fontWeight: 700,
                    borderRadius: 3,
                    boxShadow: "0 4px 14px 0 rgba(25, 118, 210, 0.39)",
                    "&:hover": {
                      background:
                        "linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)",
                      transform: "translateY(-2px)",
                      boxShadow: "0 6px 20px 0 rgba(25, 118, 210, 0.5)"
                    },
                    "&:disabled": {
                      background: "#e2e8f0"
                    },
                    transition: "all 0.2s ease-in-out"
                  }}
                >
                  {loading ? (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <CircularProgress size={20} color="inherit" />
                      <Typography variant="button">Processing...</Typography>
                    </Box>
                  ) : editData ? (
                    "Update Masterclass"
                  ) : (
                    "Create Masterclass"
                  )}
                </Button>
              </Box>
            </Box>
          </Box>
        </Box>
      </Fade>
    </Modal>
  );
};

export default CreateMasterclassModal;
